import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { transformRESTVideoV2ToSDKVideoV2 } from '@wix/sdk-runtime/transformations/video-v2';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkCreateReviewOptions,
  BulkCreateReviewResponse,
  BulkDeleteReviewsOptions,
  BulkDeleteReviewsResponse,
  BulkUpdateModerationStatusOptions,
  BulkUpdateModerationStatusResponse,
  CountReviewsApplicationErrors,
  CountReviewsOptions,
  CountReviewsResponse,
  DeleteReviewResponse,
  GetReviewOptions,
  QueryReviewsOptions,
  QueryReviewsResponse,
  RemoveReplyResponse,
  Review,
  ReviewCreatedEnvelope,
  ReviewDeletedEnvelope,
  ReviewModerationStatusChangedEnvelope,
  ReviewPublishedEnvelope,
  ReviewQuery,
  ReviewUpdatedEnvelope,
  ReviewsQueryBuilder,
  SetReplyResponse,
  UpdateModerationStatusOptions,
  UpdateModerationStatusResponse,
  UpdateReview,
  bulkCreateReview as universalBulkCreateReview,
  bulkDeleteReviews as universalBulkDeleteReviews,
  bulkUpdateModerationStatus as universalBulkUpdateModerationStatus,
  countReviews as universalCountReviews,
  createReview as universalCreateReview,
  deleteReview as universalDeleteReview,
  getReview as universalGetReview,
  queryReviews as universalQueryReviews,
  removeReply as universalRemoveReply,
  setReply as universalSetReply,
  typedQueryReviews as universalTypedQueryReviews,
  updateModerationStatus as universalUpdateModerationStatus,
  updateReview as universalUpdateReview,
} from './reviews-v1-review-reviews.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/reviews' };

export function getReview(httpClient: HttpClient): GetReviewSignature {
  return (reviewId: string, options?: GetReviewOptions) =>
    universalGetReview(
      reviewId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetReviewSignature {
  /**
   * Retrieves a review.
   *
   * By default, unpublished reviews aren't returned.
   * To retrieve an unpublished review, pass `returnPrivateReviews` as `true`.
   * @param - Review ID.
   * @param - Information about the reviews to retrieve.
   * @returns Review.
   */
  (reviewId: string, options?: GetReviewOptions): Promise<
    NonNullablePaths<
      Review,
      | `namespace`
      | `entityId`
      | `_id`
      | `content.media`
      | `content.rating`
      | `reply.message`
      | `foundHelpful`
      | `foundUnhelpful`
      | `helpfulness`
      | `moderation.moderationStatus`
      | `moderation.manuallyChanged`
      | `verified`
      | `origin.type`
      | `publishCount`,
      3
    >
  >;
}

export function createReview(httpClient: HttpClient): CreateReviewSignature {
  return (
    review: NonNullablePaths<
      Review,
      `content` | `content.rating` | `entityId` | `namespace`,
      3
    >
  ) =>
    universalCreateReview(
      review,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateReviewSignature {
  /**
   * Creates a new review using a contact ID.
   *
   * This method requires a contact ID, see the [Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/introduction).
   *
   * After a review is created, it may require moderation, see the introduction for the Reviews API for more information about moderation.
   *
   * If the `authorName` field is empty, and the review author:
   * - is a site member, their member name is used.
   * - isn't a site member, the field is set to `null`.
   * @param - Review data.
   * @returns Created review.
   */
  (
    review: NonNullablePaths<
      Review,
      `content` | `content.rating` | `entityId` | `namespace`,
      3
    >
  ): Promise<
    NonNullablePaths<
      Review,
      | `namespace`
      | `entityId`
      | `_id`
      | `content.media`
      | `content.rating`
      | `reply.message`
      | `foundHelpful`
      | `foundUnhelpful`
      | `helpfulness`
      | `moderation.moderationStatus`
      | `moderation.manuallyChanged`
      | `verified`
      | `origin.type`
      | `publishCount`,
      3
    >
  >;
}

export function bulkCreateReview(
  httpClient: HttpClient
): BulkCreateReviewSignature {
  return (
    reviews: NonNullablePaths<
      Review,
      `author` | `author.contactId` | `content` | `entityId` | `namespace`,
      3
    >[],
    options?: BulkCreateReviewOptions
  ) =>
    universalBulkCreateReview(
      reviews,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateReviewSignature {
  /**
   * Creates multiple reviews.
   *
   * If the `authorName` field is empty, and the review author:
   * - is a site member, their member name is used.
   * - isn't a site member, the field is set to `null`.
   *
   * To create a single review, call Create Review.
   * @param - Reviews to create.
   */
  (
    reviews: NonNullablePaths<
      Review,
      `author` | `author.contactId` | `content` | `entityId` | `namespace`,
      3
    >[],
    options?: BulkCreateReviewOptions
  ): Promise<
    NonNullablePaths<
      BulkCreateReviewResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.review.namespace`
      | `results.${number}.review.entityId`
      | `results.${number}.review._id`
      | `results.${number}.review.content.rating`
      | `results.${number}.review.reply.message`
      | `results.${number}.review.foundHelpful`
      | `results.${number}.review.foundUnhelpful`
      | `results.${number}.review.helpfulness`
      | `results.${number}.review.moderation.moderationStatus`
      | `results.${number}.review.moderation.manuallyChanged`
      | `results.${number}.review.verified`
      | `results.${number}.review.origin.type`
      | `results.${number}.review.publishCount`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function updateReview(httpClient: HttpClient): UpdateReviewSignature {
  return (_id: string, review: NonNullablePaths<UpdateReview, `revision`, 2>) =>
    universalUpdateReview(
      _id,
      review,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateReviewSignature {
  /**
   * Updates a review.
   *
   * Each time the review is updated, `revision` increments by 1.
   * The existing `revision` must be included when updating the review.
   * This ensures you're working with the latest review information, and it prevents unintended overwrites.
   * @param - Review ID.
   * @param - Options to use when updating a review.
   * @returns Updated review data.
   */
  (_id: string, review: NonNullablePaths<UpdateReview, `revision`, 2>): Promise<
    NonNullablePaths<
      Review,
      | `namespace`
      | `entityId`
      | `_id`
      | `content.media`
      | `content.rating`
      | `reply.message`
      | `foundHelpful`
      | `foundUnhelpful`
      | `helpfulness`
      | `moderation.moderationStatus`
      | `moderation.manuallyChanged`
      | `verified`
      | `origin.type`
      | `publishCount`,
      3
    >
  >;
}

export function deleteReview(httpClient: HttpClient): DeleteReviewSignature {
  return (reviewId: string) =>
    universalDeleteReview(
      reviewId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteReviewSignature {
  /**
   * Deletes a review.
   * @param - Review ID.
   */
  (reviewId: string): Promise<
    NonNullablePaths<
      DeleteReviewResponse,
      | `review.namespace`
      | `review.entityId`
      | `review._id`
      | `review.content.media`
      | `review.content.rating`
      | `review.reply.message`
      | `review.foundHelpful`
      | `review.foundUnhelpful`
      | `review.helpfulness`
      | `review.moderation.moderationStatus`
      | `review.moderation.manuallyChanged`
      | `review.verified`
      | `review.origin.type`
      | `review.publishCount`,
      4
    >
  >;
}

export function bulkDeleteReviews(
  httpClient: HttpClient
): BulkDeleteReviewsSignature {
  return (options?: BulkDeleteReviewsOptions) =>
    universalBulkDeleteReviews(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteReviewsSignature {
  /**
   * Deletes multiple reviews.
   *
   * All reviews that meet the specified `filter` criteria are deleted.
   *
   * To delete a single review, call Delete Review.
   * @param - Information about the reviews to delete.
   */
  (options?: BulkDeleteReviewsOptions): Promise<
    NonNullablePaths<BulkDeleteReviewsResponse, `jobId`, 2>
  >;
}

export function queryReviews(httpClient: HttpClient): QueryReviewsSignature {
  return (options?: QueryReviewsOptions) =>
    universalQueryReviews(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryReviewsSignature {
  /**
   * Creates a query to retrieve a list of reviews.
   *
   *
   * The `queryReviews()` method builds a query to retrieve a list of reviews and returns a `ReviewsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is typically used to run the query using the `find()` method.
   *
   * You can refine the query by chaining `ReviewsQueryBuilder` methods onto the query.
   *
   * `ReviewsQueryBuilder` methods enable you to sort, filter,
   * and control the results that `queryReviews()` returns.
   *
   * The `queryReviews()` method runs with the following defaults, which you can override:
   *
   * - `ascending("_createdDate")`
   * - `limit(100)`
   *
   * The methods that are chained to `queryReviews()` are applied in the order they're called. For example, if you apply `descending("_createdDate")` and then `descending("content.rating")`, the results are sorted first by the `_createdDate`, and then, if there are multiple results with the same `_createdDate`, the items are sorted by `content.rating`.
   * @param - Information about the reviews to retrieve.
   */
  (options?: QueryReviewsOptions): ReviewsQueryBuilder;
}

export function typedQueryReviews(
  httpClient: HttpClient
): TypedQueryReviewsSignature {
  return (query: ReviewQuery, options?: QueryReviewsOptions) =>
    universalTypedQueryReviews(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryReviewsSignature {
  /** */
  (query: ReviewQuery, options?: QueryReviewsOptions): Promise<
    NonNullablePaths<
      QueryReviewsResponse,
      | `reviews`
      | `reviews.${number}.namespace`
      | `reviews.${number}.entityId`
      | `reviews.${number}._id`
      | `reviews.${number}.content.rating`
      | `reviews.${number}.reply.message`
      | `reviews.${number}.foundHelpful`
      | `reviews.${number}.foundUnhelpful`
      | `reviews.${number}.helpfulness`
      | `reviews.${number}.moderation.moderationStatus`
      | `reviews.${number}.moderation.manuallyChanged`
      | `reviews.${number}.verified`
      | `reviews.${number}.origin.type`
      | `reviews.${number}.publishCount`,
      5
    >
  >;
}

export function setReply(httpClient: HttpClient): SetReplySignature {
  return (reviewId: string, message: string) =>
    universalSetReply(
      reviewId,
      message,
      // @ts-ignore
      { httpClient }
    );
}

interface SetReplySignature {
  /**
   * Sets a reply on a review.
   *
   * Only 1 reply can be left on a review. Calling Set Reply when a reply
   * already exists overwrites the existing reply.
   * @param - Review ID.
   * @param - Response to review author.
   */
  (reviewId: string, message: string): Promise<
    NonNullablePaths<
      SetReplyResponse,
      | `review.namespace`
      | `review.entityId`
      | `review._id`
      | `review.content.media`
      | `review.content.rating`
      | `review.reply.message`
      | `review.foundHelpful`
      | `review.foundUnhelpful`
      | `review.helpfulness`
      | `review.moderation.moderationStatus`
      | `review.moderation.manuallyChanged`
      | `review.verified`
      | `review.origin.type`
      | `review.publishCount`,
      4
    >
  >;
}

export function removeReply(httpClient: HttpClient): RemoveReplySignature {
  return (reviewId: string) =>
    universalRemoveReply(
      reviewId,
      // @ts-ignore
      { httpClient }
    );
}

interface RemoveReplySignature {
  /**
   * Removes reply from a review.
   * @param - ID of review to remove the reply for.
   */
  (reviewId: string): Promise<
    NonNullablePaths<
      RemoveReplyResponse,
      | `review.namespace`
      | `review.entityId`
      | `review._id`
      | `review.content.media`
      | `review.content.rating`
      | `review.reply.message`
      | `review.foundHelpful`
      | `review.foundUnhelpful`
      | `review.helpfulness`
      | `review.moderation.moderationStatus`
      | `review.moderation.manuallyChanged`
      | `review.verified`
      | `review.origin.type`
      | `review.publishCount`,
      4
    >
  >;
}

export function updateModerationStatus(
  httpClient: HttpClient
): UpdateModerationStatusSignature {
  return (reviewId: string, options?: UpdateModerationStatusOptions) =>
    universalUpdateModerationStatus(
      reviewId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateModerationStatusSignature {
  /**
   * Updates the review moderation status.
   * @param - ID of the review to moderate.
   */
  (reviewId: string, options?: UpdateModerationStatusOptions): Promise<
    NonNullablePaths<
      UpdateModerationStatusResponse,
      | `review.namespace`
      | `review.entityId`
      | `review._id`
      | `review.content.media`
      | `review.content.rating`
      | `review.reply.message`
      | `review.foundHelpful`
      | `review.foundUnhelpful`
      | `review.helpfulness`
      | `review.moderation.moderationStatus`
      | `review.moderation.manuallyChanged`
      | `review.verified`
      | `review.origin.type`
      | `review.publishCount`,
      4
    >
  >;
}

export function bulkUpdateModerationStatus(
  httpClient: HttpClient
): BulkUpdateModerationStatusSignature {
  return (options?: BulkUpdateModerationStatusOptions) =>
    universalBulkUpdateModerationStatus(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateModerationStatusSignature {
  /**
   * Updates moderation status of multiple reviews. All reviews are updated to the same moderation status.
   *
   * To update moderation status of a single review, call Update Moderation Status.
   */
  (options?: BulkUpdateModerationStatusOptions): Promise<
    NonNullablePaths<BulkUpdateModerationStatusResponse, `jobId`, 2>
  >;
}

export function countReviews(httpClient: HttpClient): CountReviewsSignature {
  return (options?: CountReviewsOptions) =>
    universalCountReviews(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CountReviewsSignature {
  /**
   * Retrieves the number of reviews that match a specified filter.
   *
   * If a filter isn't passed in the request, the method returns the count of all reviews.
   */
  (options?: CountReviewsOptions): Promise<
    NonNullablePaths<CountReviewsResponse, `count`, 2> & {
      __applicationErrorsType?: CountReviewsApplicationErrors;
    }
  >;
}

export const onReviewCreated = EventDefinition(
  'wix.reviews.v1.review_created',
  true,
  (event: ReviewCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.reviewDate' },
            { path: 'entity.reply.createdDate' },
            { path: 'entity.reply.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [{ path: 'entity.relevanceScore' }],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.content.media.image' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'entity.content.media.video' }],
        },
      ])
    )
)<ReviewCreatedEnvelope>();
export const onReviewDeleted = EventDefinition(
  'wix.reviews.v1.review_deleted',
  true,
  (event: ReviewDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.reviewDate' },
            { path: 'entity.reply.createdDate' },
            { path: 'entity.reply.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [{ path: 'entity.relevanceScore' }],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.content.media.image' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'entity.content.media.video' }],
        },
      ])
    )
)<ReviewDeletedEnvelope>();
export const onReviewModerationStatusChanged = EventDefinition(
  'wix.reviews.v1.review_moderation_status_changed',
  true,
  (event: ReviewModerationStatusChangedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.review.createdDate' },
            { path: 'data.review.updatedDate' },
            { path: 'data.review.reviewDate' },
            { path: 'data.review.reply.createdDate' },
            { path: 'data.review.reply.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [{ path: 'data.review.relevanceScore' }],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'data.review.content.media.image' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'data.review.content.media.video' }],
        },
      ])
    )
)<ReviewModerationStatusChangedEnvelope>();
export const onReviewPublished = EventDefinition(
  'wix.reviews.v1.review_review_published',
  true,
  (event: ReviewPublishedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.review.createdDate' },
            { path: 'data.review.updatedDate' },
            { path: 'data.review.reviewDate' },
            { path: 'data.review.reply.createdDate' },
            { path: 'data.review.reply.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [{ path: 'data.review.relevanceScore' }],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'data.review.content.media.image' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'data.review.content.media.video' }],
        },
      ])
    )
)<ReviewPublishedEnvelope>();
export const onReviewUpdated = EventDefinition(
  'wix.reviews.v1.review_updated',
  true,
  (event: ReviewUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.reviewDate' },
            { path: 'entity.reply.createdDate' },
            { path: 'entity.reply.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
            { path: 'modifiedFields.reviewDate' },
            { path: 'modifiedFields.reply.createdDate' },
            { path: 'modifiedFields.reply.updatedDate' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.relevanceScore' },
            { path: 'modifiedFields.relevanceScore' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.content.media.image' },
            { path: 'modifiedFields.content.media.image' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [
            { path: 'entity.content.media.video' },
            { path: 'modifiedFields.content.media.video' },
          ],
        },
      ])
    )
)<ReviewUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  ApplicationError,
  Author,
  AuthorType,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkCreateReviewOptions,
  BulkCreateReviewRequest,
  BulkCreateReviewResponse,
  BulkDeleteReviewsOptions,
  BulkDeleteReviewsRequest,
  BulkDeleteReviewsResponse,
  BulkReviewResult,
  BulkUpdateModerationStatusOptions,
  BulkUpdateModerationStatusRequest,
  BulkUpdateModerationStatusResponse,
  CountReviewsOptions,
  CountReviewsRequest,
  CountReviewsResponse,
  CreateReviewAndContactRequest,
  CreateReviewRequest,
  CreateReviewResponse,
  CursorPaging,
  Cursors,
  DeleteReviewRequest,
  DeleteReviewResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  GetReviewOptions,
  GetReviewRequest,
  GetReviewResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  Media,
  MediaMediaOneOf,
  MessageEnvelope,
  Moderation,
  ModerationModerationStatus,
  ModerationStatus,
  ModerationStatusChanged,
  Origin,
  OriginType,
  PagingMetadataV2,
  QueryReviewsOptions,
  QueryReviewsRequest,
  QueryReviewsResponse,
  QueryV2,
  RemoveReplyRequest,
  RemoveReplyResponse,
  Reply,
  RestoreInfo,
  Review,
  ReviewContent,
  ReviewCreatedEnvelope,
  ReviewDeletedEnvelope,
  ReviewModerationStatusChangedEnvelope,
  ReviewPublished,
  ReviewPublishedEnvelope,
  ReviewQuerySpec,
  ReviewRequestOptions,
  ReviewUpdatedEnvelope,
  ReviewsQueryBuilder,
  ReviewsQueryResult,
  SetReplyRequest,
  SetReplyResponse,
  SortOrder,
  Sorting,
  Source,
  SourceSourceTypeOptionsOneOf,
  SourceType,
  UpdateModerationStatusOptions,
  UpdateModerationStatusRequest,
  UpdateModerationStatusRequestModerationStatus,
  UpdateModerationStatusResponse,
  UpdateReview,
  UpdateReviewContentRequest,
  UpdateReviewContentResponse,
  UpdateReviewRequest,
  UpdateReviewResponse,
  VideoResolution,
  WebhookIdentityType,
  utils,
} from './reviews-v1-review-reviews.universal.js';
